import React from "react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { User, Camera, Settings, LogOut } from "lucide-react";
import Avatar from "./Avatar";
import PlusBadge from "./PlusBadge";

/**
 * AccountMenu — avatar pill in the top nav. Click → dropdown with profile actions.
 *
 * Props:
 *  - user: { name, artistName, avatarUrl, lastSeenAt }
 *  - onLogout: () => void
 *  - profilePath: route to "View Profile" page
 *  - settingsPath: route to "Account Settings" page
 *  - changePicTargetId: html id of an <input type="file"> on the settings page (we send the user there and trigger focus)
 *  - theme: "dark" (studio) | "light" (admin)
 *  - testIdPrefix
 */
export default function AccountMenu({
  user,
  onLogout,
  profilePath = "/studio/profile",
  settingsPath = "/studio/settings",
  onViewProfile,
  onChangePic,
  onSettings,
  theme = "dark",
  testIdPrefix = "account-menu",
}) {
  const navigate = useNavigate();
  const dark = theme === "dark";

  const handleViewProfile = () => {
    if (onViewProfile) return onViewProfile();
    return navigate(profilePath);
  };
  const handleChangePic = () => {
    if (onChangePic) return onChangePic();
    return navigate(settingsPath + "?focus=avatar");
  };
  const handleSettings = () => {
    if (onSettings) return onSettings();
    return navigate(settingsPath);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={`${testIdPrefix}-trigger`}
          className={`relative rounded-full transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 ${dark ? "focus-visible:ring-paul-yellow" : "focus-visible:ring-paul-yellow"}`}
          aria-label="Account menu"
        >
          <Avatar
            user={user}
            size="md"
            showDot
            ringColor={dark ? "border-[#10162a]" : "border-white"}
            testIdPrefix={`${testIdPrefix}-avatar`}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className={`w-60 ${dark ? "bg-[#10162a] border-white/10 text-white" : "bg-white"}`}
        data-testid={`${testIdPrefix}-content`}
      >
        <DropdownMenuLabel className={dark ? "text-white" : ""}>
          <div className="flex items-center gap-3 py-1">
            <Avatar user={user} size="sm" testIdPrefix={`${testIdPrefix}-label-avatar`} />
            <div className="min-w-0">
              <div className={`text-sm font-semibold truncate flex items-center gap-1.5 ${dark ? "text-white" : "text-navy"}`}>
                <span className="truncate">{user?.artistName || user?.name || "—"}</span>
                <PlusBadge user={user} size="xs" />
              </div>
              <div className={`text-xs truncate ${dark ? "text-white/50" : "text-gray-500"}`}>
                {user?.email || ""}
              </div>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className={dark ? "bg-white/10" : ""} />
        <DropdownMenuItem
          data-testid={`${testIdPrefix}-view-profile`}
          onClick={handleViewProfile}
          className={dark ? "focus:bg-white/10 focus:text-white cursor-pointer" : "cursor-pointer"}
        >
          <User className="w-4 h-4 mr-2" /> View Profile
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid={`${testIdPrefix}-change-pic`}
          onClick={handleChangePic}
          className={dark ? "focus:bg-white/10 focus:text-white cursor-pointer" : "cursor-pointer"}
        >
          <Camera className="w-4 h-4 mr-2" /> Upload / Change Picture
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid={`${testIdPrefix}-settings`}
          onClick={handleSettings}
          className={dark ? "focus:bg-white/10 focus:text-white cursor-pointer" : "cursor-pointer"}
        >
          <Settings className="w-4 h-4 mr-2" /> Account Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator className={dark ? "bg-white/10" : ""} />
        <DropdownMenuItem
          data-testid={`${testIdPrefix}-logout`}
          onClick={onLogout}
          className={`${dark ? "focus:bg-white/10 focus:text-white" : ""} text-red-500 focus:text-red-500 cursor-pointer`}
        >
          <LogOut className="w-4 h-4 mr-2" /> Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
